/**
 * Tiny inline SVG sparkline — no chart library. Draws a smooth area+line from a
 * real number series (Spec 11: "number + sparkline" card type). Color follows
 * the accent token by default.
 *
 * Fluid by default (founder 2026-06-13 cleanup: cards "overlapping their
 * containers"): it measures its own container and fills the available width at a
 * fixed height, so a sparkline can never spill past a narrow grid cell. The
 * `width` prop is now just the FALLBACK width used before first measure / when
 * there's no container box.
 */

import { useEffect, useRef, useState } from "react";

export function Sparkline({
  data,
  width = 120,
  height = 36,
  color = "var(--accent)",
  strokeWidth = 1.75,
}: {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  strokeWidth?: number;
}): React.ReactElement | null {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const [w, setW] = useState(width);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect.width;
      if (cw && cw > 0) setW(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data || data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pad = 2;
  const iw = w - pad * 2;
  const h = height - pad * 2;

  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * iw;
    const y = pad + h - ((v - min) / range) * h;
    return [x, y] as const;
  });

  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  const first = pts[0];
  if (!last || !first) return null;
  const area = `${line} L${last[0].toFixed(1)},${height} L${first[0].toFixed(1)},${height} Z`;
  const gradId = `dd-spark-${Math.round(min)}-${Math.round(max)}-${data.length}`;

  // Wrapper is width:100% so the ResizeObserver tracks the real cell width; the
  // SVG is rendered at the measured pixel width (uniform scale, no distortion).
  return (
    <span ref={wrapRef} style={{ display: "block", width: "100%" }}>
      <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} aria-hidden="true" style={{ display: "block" }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradId})`} />
        <path d={line} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={last[0]} cy={last[1]} r={2.4} fill={color} />
      </svg>
    </span>
  );
}
